import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { INCIDENT_TYPES, SEVERITIES, type Incident } from "@/contracts";

import { incidentsQuery, type IncidentFilters } from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { useTranslation } from "react-i18next";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { IncidentStatusBadge, SeverityBadge } from "@/features/devices/device-badges";
import {
  INCIDENT_TYPE_LABELS,
  SEVERITY,
  formatIncidentValue,
} from "@/features/devices/device-labels";
import { OrganizationFilter } from "@/features/organizations/organization-filter";
import { useApiClient } from "@/lib/api-context";
import { formatDateTime, formatRelative } from "@/lib/format";
import { useListFilters } from "@/lib/use-list-filters";
import { AcknowledgeDialog } from "./acknowledge-dialog";

/**
 * One control instead of two.
 *
 * `status` and `unresolved` can contradict each other at the API, where an
 * explicit status wins. Exposing both as separate controls would let someone
 * build a filter that quietly ignores half of what they set, so the screen
 * offers the four views that actually make sense.
 */
const VIEW_FILTERS = {
  "": { filters: { unresolved: true } },
  OPEN: { filters: { status: "OPEN" } },
  ACKNOWLEDGED: { filters: { status: "ACKNOWLEDGED" } },
  RESOLVED: { filters: { status: "RESOLVED" } },
  ALL: { filters: { unresolved: false } },
} as const satisfies Record<string, { filters: Partial<IncidentFilters> }>;

const VIEW_LABEL_KEYS = {
  "": "incidentViewNeedsAttention",
  OPEN: "incidentViewOpen",
  ACKNOWLEDGED: "incidentViewAcknowledged",
  RESOLVED: "incidentViewResolved",
  ALL: "incidentViewAll",
} as const;

type ViewKey = keyof typeof VIEW_FILTERS;

function viewOf(value: string): ViewKey {
  return value in VIEW_FILTERS ? (value as ViewKey) : "";
}

export function IncidentsPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const { value: filterValue, page, setFilter, setPage } = useListFilters();
  const [acknowledging, setAcknowledging] = useState<Incident | null>(null);

  const view = viewOf(filterValue("view"));

  const filters: IncidentFilters = {
    ...VIEW_FILTERS[view].filters,
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
    ...(filterValue("type") ? { type: filterValue("type") } : {}),
    ...(filterValue("severity") ? { severity: filterValue("severity") } : {}),
    ...(filterValue("organizationId") ? { organizationId: filterValue("organizationId") } : {}),
    page,
  };

  const client = useApiClient();
  const incidents = useQuery(incidentsQuery(client, filters));

  return (
    <>
      <PageHeader
        title={t("incidentsTitle")}
        description={t("incidentsDescription")}
      />

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SearchInput
              value={filters.search ?? ""}
              placeholder={t("searchIncidents")}
              onChange={(value) => setFilter("search", value)}
            />

            <SelectField
              label={tCommon("show")}
              value={view}
              onChange={(value) => setFilter("view", value)}
              data={(Object.keys(VIEW_FILTERS) as ViewKey[]).map((key) => ({
                value: key,
                label: t(VIEW_LABEL_KEYS[key]),
              }))}
            />

            <SelectField
              label={tCommon("type")}
              value={filters.type ?? ""}
              onChange={(value) => setFilter("type", value)}
              data={[
                { value: "", label: tCommon("all") },
                ...INCIDENT_TYPES.map((type) => ({
                  value: type,
                  label: INCIDENT_TYPE_LABELS[type],
                })),
              ]}
            />

            <SelectField
              label={tCommon("severity")}
              value={filters.severity ?? ""}
              onChange={(value) => setFilter("severity", value)}
              data={[
                { value: "", label: tCommon("all") },
                ...SEVERITIES.map((severity) => ({
                  value: severity,
                  label: SEVERITY[severity].label,
                })),
              ]}
            />

            {isSuperAdmin ? (
              <OrganizationFilter
                value={filters.organizationId ?? ""}
                onChange={(value) => setFilter("organizationId", value)}
              />
            ) : null}
          </div>
        </CardBody>
      </Card>

      {incidents.isPending ? <LoadingBlock label={t("loadingIncidents")} /> : null}

      {incidents.isError ? (
        <ErrorBlock error={incidents.error} onRetry={() => void incidents.refetch()} />
      ) : null}

      {incidents.data ? (
        <Card>
          <DataTable caption={t("incidentsTitle")}>
            <thead>
              <tr>
                <Th>{t("colAsset")}</Th>
                {isSuperAdmin ? <Th>{t("colOrganization")}</Th> : null}
                <Th>{t("colType")}</Th>
                <Th>{t("colSeverity")}</Th>
                <Th>{t("colStatus")}</Th>
                <Th numeric>{t("colReading")}</Th>
                <Th numeric>{t("colThreshold")}</Th>
                <Th>{t("colStarted")}</Th>
                <Th>
                  <span className="sr-only">{t("colActions")}</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {incidents.data.data.length === 0 ? (
                <EmptyRow colSpan={isSuperAdmin ? 9 : 8}>
                  {view === "" ? t("emptyIncidentsClear") : t("emptyIncidents")}
                </EmptyRow>
              ) : (
                incidents.data.data.map((incident) => (
                  <IncidentRow
                    key={incident.id}
                    incident={incident}
                    showOrganization={isSuperAdmin}
                    onAcknowledge={() => setAcknowledging(incident)}
                  />
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <Td colSpan={isSuperAdmin ? 9 : 8} className="border-b-0">
                  <Pagination
                    page={incidents.data.pagination.page}
                    totalPages={incidents.data.pagination.totalPages}
                    total={incidents.data.pagination.total}
                    noun={{ one: tCommon("noun.incident.one"), other: tCommon("noun.incident.other") }}
                    onChange={setPage}
                  />
                </Td>
              </tr>
            </tfoot>
          </DataTable>
        </Card>
      ) : null}

      {acknowledging ? (
        <AcknowledgeDialog incident={acknowledging} onClose={() => setAcknowledging(null)} />
      ) : null}
    </>
  );
}

function IncidentRow({
  incident,
  showOrganization,
  onAcknowledge,
}: {
  incident: Incident;
  showOrganization: boolean;
  onAcknowledge: () => void;
}) {
  const { t } = useTranslation("pages");
  return (
    <tr>
      <Td>
        <Link
          to={`/devices/${incident.deviceId}`}
          className="font-medium text-brand-strong underline underline-offset-2"
        >
          {incident.assetName ?? incident.devEui}
        </Link>
        <span className="block text-xs text-ink-muted">{incident.siteName ?? t("noSite")}</span>
      </Td>
      {showOrganization ? <Td>{incident.organizationName}</Td> : null}
      <Td>{INCIDENT_TYPE_LABELS[incident.type]}</Td>
      <Td>
        <SeverityBadge value={incident.severity} />
      </Td>
      <Td>
        <IncidentStatusBadge value={incident.status} />
        {incident.acknowledgedByName ? (
          <span className="block text-xs text-ink-muted">
            {t("acknowledgedBy", { name: incident.acknowledgedByName })}
          </span>
        ) : null}
      </Td>
      <Td numeric>{formatIncidentValue(incident.type, incident.triggerValue)}</Td>
      <Td numeric>{formatIncidentValue(incident.type, incident.thresholdValue)}</Td>
      <Td>
        <span title={formatDateTime(incident.startedAt)}>{formatRelative(incident.startedAt)}</span>
        {incident.resolvedAt ? (
          <span className="block text-xs text-ink-muted">
            {t("incidentViewResolved")} {formatRelative(incident.resolvedAt)}
          </span>
        ) : null}
      </Td>
      <Td>
        {incident.status === "OPEN" ? (
          <Button variant="secondary" size="sm" onClick={onAcknowledge}>
            {t("acknowledge")}
          </Button>
        ) : null}
      </Td>
    </tr>
  );
}
